import React, { useState } from 'react'
import CustomSelect from '../../../../components/CustomSelect/CustomSelect'

/**
 * 💰 Базовые тарифы — фикс и сдельная часть.
 * 
 * Включает: ставка за смену / почасовая, бонусы за приёмку/выдачу/возврат, % от оборота.
 */
export default function BasicRatesSection({ settings, onChange }) {
  // Инициализируем локальный режим фикса на основе текущих настроек
  const [fixMode, setFixMode] = useState(() => {
    return (parseFloat(settings.hourlyRate) > 0 && !parseFloat(settings.dailyRate)) ? 'hourly' : 'shift'
  })

  const handleFixModeChange = (mode) => {
    setFixMode(mode)
  }

  const handleDailyRateChange = (val) => {
    onChange('dailyRate', val)
    // Если пользователь редактирует ставку за смену, 
    // сбрасываем почасовую ставку (только если она не 0)
    if (parseFloat(settings.hourlyRate) !== 0) {
      onChange('hourlyRate', 0)
    }
  }

  const handleHourlyRateChange = (val) => {
    onChange('hourlyRate', val)
    // Если пользователь редактирует почасовую ставку, 
    // сбрасываем ставку за смену (только если она не 0)
    if (parseFloat(settings.dailyRate) !== 0) {
      onChange('dailyRate', 0)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Выбор режима фиксированной части */}
      <div>
        <div style={{
          fontSize: '13px', fontWeight: 700, color: 'var(--text-tertiary)',
          textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px'
        }}>
          Фиксированная часть
        </div>

        {/* Переключатель режима */}
        <div style={{
          display: 'flex', gap: '8px', marginBottom: '14px',
        }}>
          {[
            { id: 'shift', label: 'За смену (выход)', icon: '📅' },
            { id: 'hourly', label: 'За часы', icon: '⏱' },
          ].map(mode => {
            const isActive = fixMode === mode.id
            return (
              <button
                key={mode.id}
                type="button"
                onClick={() => handleFixModeChange(mode.id)}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  borderRadius: '10px',
                  border: isActive ? '2px solid var(--wb-primary)' : '2px solid var(--border-color)',
                  background: isActive
                    ? 'color-mix(in srgb, var(--wb-primary) 8%, transparent)'
                    : 'var(--surface-color)',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.2s ease',
                  fontSize: '13px',
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? 'var(--wb-primary)' : 'var(--text-secondary)',
                }}
              >
                <span style={{ marginRight: '6px' }}>{mode.icon}</span>
                {mode.label}
              </button>
            )
          })}
        </div>

        {/* Поле ставки в зависимости от режима */}
        <div className="salary-config-grid" style={{ marginBottom: 0 }}>
          {fixMode === 'shift' ? (
            <div className="salary-config-item">
              <div className="salary-config-icon"></div>
              <div className="salary-config-field">
                <label>Ставка за смену</label>
                <div className="salary-config-input-wrapper">
                  <input
                    className="input"
                    type="number"
                    min="0"
                    value={settings.dailyRate === 0 || settings.dailyRate === '0' ? '' : settings.dailyRate}
                    placeholder="0"
                    onChange={(e) => handleDailyRateChange(e.target.value)}
                  />
                  <span className="salary-config-unit">₽ / смена</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="salary-config-item">
              <div className="salary-config-icon"></div>
              <div className="salary-config-field">
                <label>Почасовая ставка</label>
                <div className="salary-config-input-wrapper">
                  <input
                    className="input"
                    type="number"
                    min="0"
                    value={settings.hourlyRate === 0 || settings.hourlyRate === '0' ? '' : settings.hourlyRate}
                    placeholder="0"
                    onChange={(e) => handleHourlyRateChange(e.target.value)}
                  />
                  <span className="salary-config-unit">₽ / час</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Сдельная часть */}
      <div>
        <div style={{
          fontSize: '13px', fontWeight: 700, color: 'var(--text-tertiary)',
          textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px'
        }}>
          Сдельная часть
        </div>
        <div className="salary-config-grid">
          <div className="salary-config-item">
            <div className="salary-config-icon"></div>
            <div className="salary-config-field">
              <label>За принятый товар</label>
              <div className="salary-config-input-wrapper">
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="0.1"
                  value={settings.bonusPerAccepted === 0 || settings.bonusPerAccepted === '0' ? '' : settings.bonusPerAccepted}
                  placeholder="0"
                  onChange={(e) => onChange('bonusPerAccepted', e.target.value)}
                />
                <span className="salary-config-unit">₽ / шт</span>
              </div>
            </div>
          </div>

          <div className="salary-config-item">
            <div className="salary-config-icon"></div>
            <div className="salary-config-field">
              <label>За выданный товар</label>
              <div className="salary-config-input-wrapper">
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="0.1"
                  value={settings.bonusPerIssued === 0 || settings.bonusPerIssued === '0' ? '' : settings.bonusPerIssued}
                  placeholder="0"
                  onChange={(e) => onChange('bonusPerIssued', e.target.value)}
                />
                <span className="salary-config-unit">₽ / шт</span>
              </div>
            </div>
          </div>

          <div className="salary-config-item">
            <div className="salary-config-icon"></div>
            <div className="salary-config-field">
              <label>За возврат товара</label>
              <div className="salary-config-input-wrapper">
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="0.1"
                  value={settings.bonusPerReturned === 0 || settings.bonusPerReturned === '0' ? '' : settings.bonusPerReturned}
                  onChange={(e) => onChange('bonusPerReturned', e.target.value)}
                  placeholder="0"
                />
                <span className="salary-config-unit">₽ / шт</span>
              </div>
            </div>
          </div>

          <div className="salary-config-item salary-config-item-disabled" style={{ opacity: 0.75 }}>
            <div className="salary-config-icon"></div>
            <div className="salary-config-field">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ margin: 0 }}>Процент от оборота ПВЗ</label>
                <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '4px', background: '#f3f4f6', color: '#6b7280', fontWeight: 500 }}>
                  В разработке
                </span>
              </div>
              <div className="salary-config-input-wrapper" style={{ marginTop: '4px' }}>
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="0.1"
                  disabled
                  value={settings.turnoverPercent === 0 || settings.turnoverPercent === '0' ? '' : settings.turnoverPercent}
                  onChange={(e) => onChange('turnoverPercent', e.target.value)}
                  placeholder="0"
                  style={{ backgroundColor: '#f9fafb', cursor: 'not-allowed', color: '#9ca3af' }}
                />
                <span className="salary-config-unit">%</span>
              </div>
              <p className="salary-config-help" style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>
                В разработке — сейчас не участвует в расчёте зарплаты
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
